import LinearProgress from '@mui/material/LinearProgress';
import classNames from 'classnames';

import { useStyles } from './LinearProgressBar.styles';

/**
 * Свойства компонента {@link LinearProgressBar}
 */
export interface LinearProgressProps {
  /**
   * Вариант отображения прогресса.
   *
   * @defaultValue `'indeterminate'`
   */
  variant?: 'determinate' | 'indeterminate' | 'buffer' | 'query';

  /**
   * Значение прогресса (0-100) для determinate варианта.
   */
  value?: number;

  /**
   * Значение буфера (0-100) для buffer варианта.
   */
  valueBuffer?: number;

  /**
   * Цвет линии прогресса.
   */
  color?: string;

  /**
   * Высота индикатора прогресса (в пикселях как число или в любых CSS-единицах как строка).
   */
  height?: number | string;

  /**
   * Определяет, должен ли фон быть прозрачным.
   *
   * @defaultValue `false`
   */
  transparentBackground?: boolean;

  /**
   * Дополнительные CSS классы для применения к индикатору прогресса.
   */
  className?: string;
}

/**
 * Компонент, отображающий линейный индикатор прогресса с настраиваемым вариантом, цветом и высотой.
 *
 * @public
 * @param variant - Вариант отображения прогресса.
 * @param value - Значение прогресса (0-100) для determinate варианта.
 * @param valueBuffer - Значение буфера (0-100) для buffer варианта.
 * @param color - Цвет линии прогресса.
 * @param height - Высота индикатора прогресса (в пикселях как число или в любых CSS-единицах как строка).
 * @param transparentBackground - Определяет, должен ли фон быть прозрачным.
 * @param className - Дополнительные CSS классы для применения к индикатору прогресса.
 * @returns Элемент LinearProgress.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link LinearProgressProps}
 */
export const LinearProgressBar = ({
  variant = 'indeterminate',
  value,
  valueBuffer,
  color,
  height,
  transparentBackground = false,
  className,
  ...rest
}: LinearProgressProps) => {
  const { classes } = useStyles({
    height,
    color,
    transparentBackground,
  });

  return (
    <LinearProgress
      {...rest}
      data-testid="linear-progress"
      variant={variant}
      value={value}
      valueBuffer={valueBuffer}
      className={classNames(classes.root, className, 'aqa_linear_progress')}
    />
  );
};
